<template>
  <div id="reportPage">
    <main>
      <article>
        <div v-if="loading" class="pw-loading-overlay" aria-live="polite" aria-busy="true">
          <div class="pw-loading-card">
            <div class="pw-spinner" />
            <div class="pw-loading-text">Loading…</div>
          </div>
        </div>

        <div class="title-wrapper mb-4">
          <h2 class="login-title">AGENT PERFORMANCE</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>

        <div class="tabs">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: tab === 'daily' }"
            @click="switchTab('daily')"
          >Daily</button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: tab === 'weekly' }"
            @click="switchTab('weekly')"
          >Weekly</button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: tab === 'hours' }"
            @click="switchTab('hours')"
          >Hours &amp; Attendance</button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: tab === 'bonuses' }"
            @click="switchTab('bonuses')"
          >Bonuses</button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: tab === 'history' }"
            @click="switchTab('history')"
          >History</button>
        </div>

        <div class="toolbar panel">
          <label>
            From
            <input v-model="dateFrom" type="date" class="form-control" />
          </label>
          <label>
            To
            <input v-model="dateTo" type="date" class="form-control" />
          </label>
          <label v-if="tab !== 'history'">
            Team
            <select v-model="teamId" class="form-select">
              <option value="">All teams</option>
              <option v-for="t in teams" :key="t.id" :value="String(t.id)">{{ t.name }}</option>
            </select>
          </label>
          <label v-if="tab !== 'history'">
            Agent
            <input v-model="agentFilter" class="form-control" placeholder="Filter by name/id" />
          </label>
          <label v-if="tab === 'history'">
            Category
            <select v-model="historyCategory" class="form-select">
              <option value="">All changes</option>
              <option
                v-for="c in historyCategories"
                :key="c.id"
                :value="c.id"
              >{{ c.label }}</option>
            </select>
          </label>
          <label v-if="tab === 'history'">
            Search
            <input v-model="historySearch" class="form-control" placeholder="avatar / call / text" />
          </label>
          <button type="button" class="btn btn-primary load-btn" :disabled="loading" @click="loadPerformance">
            {{ loading ? "Loading…" : "Load" }}
          </button>
          <div class="toolbar-live">
            <AutoRefresh v-model="refreshRate" @refresh="onLiveRefresh" />
            <span class="ap-updated-dim" aria-live="polite">
              Last updated {{ lastLoadedLabel || "—" }} ET
              <template v-if="lastCgReportsLabel">
                · CGReports {{ lastCgReportsLabel }} ET
              </template>
              <template v-if="liveRefreshing"> · Refreshing…</template>
            </span>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3">{{ errorMessage }}</div>
        <div v-if="okMessage" class="alert alert-success mt-3">{{ okMessage }}</div>

        <div
          v-if="tab === 'history'"
          class="history-wrap mt-3"
        >
          <div class="table-responsive">
            <table class="table table-bordered align-middle ap-table history-table">
              <thead class="table-light">
                <tr>
                  <th>When (ET)</th>
                  <th>User</th>
                  <th>Category</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in historyRecords" :key="'hist-' + h.id">
                  <td class="nowrap">{{ formatNyTime(h.createdAt) || h.createdAt || "—" }}</td>
                  <td>{{ h.username || "—" }}</td>
                  <td>
                    <span class="history-cat">{{ historyCategoryLabel(h.category) }}</span>
                  </td>
                  <td class="history-action">{{ h.action }}</td>
                </tr>
                <tr v-if="!historyRecords.length">
                  <td colspan="4" class="text-center text-muted py-3">
                    {{ loading ? "Loading…" : "No change history in this range" }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="meta-row" v-if="perf && tab !== 'hours' && tab !== 'bonuses' && tab !== 'history'">
          <span>Unmatched transfers: <strong>{{ unmatchedList.length || perf.unmatchedCount || 0 }}</strong></span>
          <button
            v-if="(unmatchedList.length || perf.unmatchedCount)"
            type="button"
            class="btn btn-sm btn-outline-primary unmatched-toggle"
            @click="showUnmatchedPanel = !showUnmatchedPanel"
          >
            {{ showUnmatchedPanel ? "Hide list" : "Assign agents" }}
          </button>
        </div>

        <div
          v-if="perf && tab !== 'hours' && tab !== 'bonuses' && tab !== 'history' && showUnmatchedPanel"
          class="unmatched-panel panel"
        >
          <div v-if="!unmatchedList.length" class="text-muted" style="font-size: 12px">
            No unmatched transfers in this period.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm table-bordered align-middle mb-0 unmatched-table">
              <thead class="table-light">
                <tr>
                  <th>Call DT</th>
                  <th>Call ID</th>
                  <th>Target</th>
                  <th>Disposition</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in unmatchedList" :key="u.inboundCallId || (u.callDt + u.targetNumber)">
                  <td>{{ u.callDt || "—" }}</td>
                  <td class="mono-id">{{ u.inboundCallId || "—" }}</td>
                  <td>
                    <div>{{ u.targetName || "—" }}</div>
                    <small class="text-muted">{{ u.targetNumber || "" }}</small>
                  </td>
                  <td>{{ u.portalDisposition || "—" }}</td>
                  <td class="text-end">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      @click="openAssignAgentModal(u)"
                    >Assign</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="perf && tab !== 'hours' && tab !== 'bonuses' && tab !== 'history'"
          class="disposition-totals panel"
        >
          <strong>Disposition counts</strong>
          <div v-if="dispositionColumns.length" class="disp-chips">
            <span
              v-for="code in dispositionColumns"
              :key="'tot-' + code"
              class="disp-chip"
            >
              <span class="disp-code">{{ code }}</span>
              <span class="disp-n">{{ dispositionTotal(code) }}</span>
            </span>
          </div>
          <div v-else class="text-muted" style="font-size: 12px">
            No disposition codes in this period (check portal disposition on Ringba Calls).
          </div>
        </div>

        <div v-if="tab === 'bonuses'" class="bonuses-wrap mt-3">
          <div class="bonus-rules panel">
            <strong>Rules</strong>
            <span>Daily: 0–3 → $0 · 4 → $6 · each billable above 4 → +$3</span>
            <span>Weekly BPH: &lt;0.36 forfeit · 0.36–0.40 → 50% · ≥0.40 → 100%</span>
            <span>Week forfeit: unauth. absence / NCNS / suspension / grave / 2+ minors</span>
            <span>Day forfeit: 1 minor → that day’s bonus · Power Hour excluded</span>
          </div>

          <div class="table-responsive">
            <table class="table table-bordered align-middle ap-table">
              <thead class="table-light">
                <tr>
                  <th>Agent</th>
                  <th>Team</th>
                  <th>Eligible daily</th>
                  <th>Week BPH</th>
                  <th>Multiplier</th>
                  <th>Adjustments</th>
                  <th>Final bonus</th>
                  <th>Reasons</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredBonusAgents" :key="'b-' + row.avatarId">
                  <td>
                    <div class="agent-cell">
                      <strong>{{ row.fullName || row.username || row.avatarId }}</strong>
                      <small>{{ row.avatarId }}</small>
                    </div>
                  </td>
                  <td>{{ row.teamName || "—" }}</td>
                  <td>${{ fmtMoney(bonusEligibleSum(row)) }}</td>
                  <td>{{ fmtBph(bonusPrimaryWeek(row)?.weeklyBph) }}</td>
                  <td>{{ bonusPrimaryWeek(row)?.bphMultiplier ?? "—" }}</td>
                  <td>${{ fmtMoney(bonusAdjSum(row)) }}</td>
                  <td><strong>${{ fmtMoney(row.finalBonus) }}</strong></td>
                  <td class="bonus-reasons">
                    <span v-for="(r, i) in bonusReasons(row)" :key="i" class="reason-chip">{{ r }}</span>
                    <span v-if="!bonusReasons(row).length" class="text-muted">—</span>
                  </td>
                </tr>
                <tr v-if="!filteredBonusAgents.length">
                  <td colspan="8" class="text-center py-3">Load bonuses for the selected dates.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="bonus-manage panel mt-3">
            <h3 class="section-title">Infractions</h3>
            <div class="bonus-form">
              <select v-model="infForm.avatarId" class="form-select">
                <option value="">Agent…</option>
                <option v-for="a in hourAgents" :key="'inf-a-' + a.avatarId" :value="a.avatarId">
                  {{ a.fullName || a.username || a.avatarId }}
                </option>
              </select>
              <input v-model="infForm.workDate" type="date" class="form-control" />
              <select v-model="infForm.severity" class="form-select">
                <option value="minor">Minor</option>
                <option value="grave">Grave</option>
              </select>
              <input v-model="infForm.infractionType" class="form-control" placeholder="Type" />
              <input v-model="infForm.notes" class="form-control" placeholder="Notes" />
              <button type="button" class="btn btn-primary" :disabled="savingInf" @click="saveInfraction">
                {{ savingInf ? "Saving…" : "Add infraction" }}
              </button>
            </div>
            <div class="table-responsive mt-2">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Date</th><th>Agent</th><th>Severity</th><th>Type</th><th>Notes</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="inf in bonusInfractions" :key="'inf-' + inf.id">
                    <td>{{ inf.workDate }}</td>
                    <td>{{ agentName(inf.avatarId) }}</td>
                    <td>
                      <span :class="inf.severity === 'grave' ? 'sev-grave' : 'sev-minor'">
                        {{ inf.severity }}
                      </span>
                    </td>
                    <td>{{ inf.infractionType }}</td>
                    <td>{{ inf.notes || "—" }}</td>
                    <td>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deleteInfraction(inf.id)">
                        Delete
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!bonusInfractions.length">
                    <td colspan="6" class="text-center text-muted">No infractions in range</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 class="section-title mt-3">Manual adjustments</h3>
            <div class="bonus-form">
              <select v-model="adjForm.avatarId" class="form-select">
                <option value="">Agent…</option>
                <option v-for="a in hourAgents" :key="'adj-a-' + a.avatarId" :value="a.avatarId">
                  {{ a.fullName || a.username || a.avatarId }}
                </option>
              </select>
              <input v-model="adjForm.periodFrom" type="date" class="form-control" />
              <input v-model="adjForm.periodTo" type="date" class="form-control" />
              <input v-model.number="adjForm.amount" type="number" step="0.01" class="form-control" placeholder="Amount $" />
              <input v-model="adjForm.reason" class="form-control" placeholder="Reason" />
              <button type="button" class="btn btn-primary" :disabled="savingAdj" @click="saveAdjustment">
                {{ savingAdj ? "Saving…" : "Add adjustment" }}
              </button>
            </div>
            <div class="table-responsive mt-2">
              <table class="table table-sm table-bordered">
                <thead class="table-light">
                  <tr>
                    <th>Agent</th><th>From</th><th>To</th><th>Amount</th><th>Reason</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="adj in bonusAdjustments" :key="'adj-' + adj.id">
                    <td>{{ agentName(adj.avatarId) }}</td>
                    <td>{{ adj.periodFrom }}</td>
                    <td>{{ adj.periodTo }}</td>
                    <td>${{ fmtMoney(adj.amount) }}</td>
                    <td>{{ adj.reason || "—" }}</td>
                    <td>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deleteAdjustment(adj.id)">
                        Delete
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!bonusAdjustments.length">
                    <td colspan="6" class="text-center text-muted">No adjustments in range</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="tab !== 'hours' && tab !== 'bonuses' && tab !== 'history'" class="table-responsive mt-3">
          <table class="table table-bordered align-middle ap-table">
            <thead class="table-light">
              <tr>
                <th rowspan="2">Agent</th>
                <th rowspan="2">Team</th>
                <th
                  v-for="p in periods"
                  :key="'h-' + p.key"
                  colspan="3"
                  class="text-center period-head"
                >{{ p.label }}</th>
                <th rowspan="2" class="transfers-head" title="Click a number for disposition breakdown">Transfers</th>
                <th rowspan="2">Billables</th>
                <th rowspan="2">Hours</th>
                <th rowspan="2">Overall BPH</th>
              </tr>
              <tr>
                <template v-for="p in periods" :key="'s-' + p.key">
                  <th>Hrs</th>
                  <th>Bill</th>
                  <th>BPH</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredAgents" :key="row.avatarId">
                <td>
                  <div class="agent-cell">
                    <strong>{{ row.fullName || row.username || row.avatarId }}</strong>
                    <small>{{ row.username }} · {{ row.avatarId }}</small>
                  </div>
                </td>
                <td>{{ row.teamName || "—" }}</td>
                <template v-for="p in periods" :key="row.avatarId + p.key">
                  <td :class="{ 'hours-missing-cell': cell(row, p.key).hoursMissing }">
                    {{ fmtNum(cell(row, p.key).hours) }}
                    <span v-if="cell(row, p.key).hoursMissing" class="missing-flag" title="Missing hours">!</span>
                  </td>
                  <td class="transfers-cell">
                    <button
                      type="button"
                      class="transfers-btn"
                      :class="{ 'transfers-btn-muted': !cellDrillable(row, p.key) }"
                      :disabled="!cellDrillable(row, p.key)"
                      :title="cellDrillable(row, p.key) ? ('View calls for ' + (p.label || p.key)) : 'No calls'"
                      @click.stop="openCallsDrill(row, { period: p })"
                    >{{ cell(row, p.key).billables }}</button>
                  </td>
                  <td :class="bphClass(cell(row, p.key).bph)">{{ fmtBph(cell(row, p.key).bph) }}</td>
                </template>
                <td class="transfers-cell">
                  <button
                    v-if="Number(row.totalTransfers || 0)"
                    type="button"
                    class="transfers-btn"
                    :title="'View calls & dispositions for ' + (row.fullName || row.username || row.avatarId)"
                    @click="openCallsDrill(row)"
                  >{{ row.totalTransfers }}</button>
                  <span v-else class="text-muted">0</span>
                </td>
                <td class="transfers-cell">
                  <button
                    v-if="Number(row.totalBillables || 0)"
                    type="button"
                    class="transfers-btn"
                    title="View billable calls"
                    @click="openCallsDrill(row, { billableOnly: true })"
                  >{{ row.totalBillables }}</button>
                  <span v-else>0</span>
                </td>
                <td>{{ fmtNum(row.totalHours) }}</td>
                <td :class="bphClass(row.overallBph)"><strong>{{ fmtBph(row.overallBph) }}</strong></td>
              </tr>
              <tr v-if="!filteredAgents.length">
                <td
                  :colspan="6 + periods.length * 3"
                  class="text-center py-3"
                >No data</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-if="assignAgentModal"
          class="ap-modal-overlay"
          @click.self="closeAssignAgentModal"
        >
          <div class="ap-modal" role="dialog" aria-modal="true" aria-labelledby="assign-agent-title">
            <header class="ap-modal-head">
              <div>
                <h3 id="assign-agent-title">Assign agent</h3>
                <p>Call {{ assignAgentModal.inboundCallId || "—" }}</p>
              </div>
              <button type="button" class="ap-modal-close" aria-label="Close" @click="closeAssignAgentModal">×</button>
            </header>
            <div class="ap-modal-body">
              <div class="assign-meta">
                <div><strong>When:</strong> {{ assignAgentModal.callDt || "—" }}</div>
                <div><strong>Target:</strong> {{ assignAgentModal.targetName || "—" }} / {{ assignAgentModal.targetNumber || "—" }}</div>
                <div><strong>Disposition:</strong> {{ assignAgentModal.portalDisposition || "—" }}</div>
              </div>
              <label class="assign-field">
                <span>Filter</span>
                <input
                  v-model="assignAgentFilter"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Name / username / id"
                  :disabled="assignAgentSubmitting"
                />
              </label>
              <label class="assign-field">
                <span>Agent</span>
                <select
                  v-model="assignAgentChoice"
                  class="form-select form-select-sm"
                  :disabled="assignAgentSubmitting"
                >
                  <option value="" disabled>Select agent</option>
                  <option
                    v-for="a in filteredAssignAgents"
                    :key="a.avatarId"
                    :value="a.avatarId"
                  >{{ agentOptionLabel(a) }}</option>
                </select>
              </label>
              <div class="ap-modal-actions">
                <button
                  type="button"
                  class="btn btn-secondary btn-sm"
                  :disabled="assignAgentSubmitting"
                  @click="closeAssignAgentModal"
                >Cancel</button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="assignAgentSubmitting || !assignAgentChoice"
                  @click="submitAssignAgent"
                >{{ assignAgentSubmitting ? "Saving…" : "Assign" }}</button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="callsModal"
          class="ap-modal-overlay"
          @click.self="closeCallsDrill"
        >
          <div
            class="ap-modal ap-modal-wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calls-drill-title"
          >
            <header class="ap-modal-head">
              <div>
                <h3 id="calls-drill-title">{{ callsModal.agentName }}</h3>
                <p>
                  {{ callsModal.periodLabel }}
                  ·
                  <template v-if="callsModal.loading">Loading calls…</template>
                  <template v-else>
                    {{ callsModal.total }} transfers
                    · {{ callsModal.billables }} billable
                  </template>
                </p>
              </div>
              <button type="button" class="ap-modal-close" aria-label="Close" @click="closeCallsDrill">×</button>
            </header>
            <div class="ap-modal-body">
              <div v-if="callsModal.dispItems.length" class="disp-chips ap-modal-chips">
                <span
                  v-for="item in callsModal.dispItems"
                  :key="'cd-' + item.code"
                  class="disp-chip"
                >
                  <span class="disp-code">{{ item.code }}</span>
                  <span class="disp-n">{{ item.count }}</span>
                </span>
              </div>

              <div class="calls-drill-toolbar">
                <div class="calls-drill-filters">
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="callsModal.billableOnly ? 'btn-outline-secondary' : 'btn-primary'"
                    :disabled="callsModal.loading"
                    @click="setCallsBillableOnly(false)"
                  >All</button>
                  <button
                    type="button"
                    class="btn btn-sm"
                    :class="callsModal.billableOnly ? 'btn-primary' : 'btn-outline-secondary'"
                    :disabled="callsModal.loading"
                    @click="setCallsBillableOnly(true)"
                  >Billable only</button>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  @click="openInRingbaCalls"
                >Open in Ringba Calls</button>
              </div>

              <div v-if="callsModal.error" class="alert alert-danger py-2 px-3 mb-2">{{ callsModal.error }}</div>
              <div v-else-if="callsModal.loading" class="text-muted" style="font-size: 12px">Fetching calls…</div>
              <div v-else class="table-responsive calls-drill-table-wrap">
                <table class="table table-sm table-bordered align-middle mb-0 calls-drill-table">
                  <thead class="table-light">
                    <tr>
                      <th>Call DT</th>
                      <th>Lead</th>
                      <th>Target</th>
                      <th>Disp</th>
                      <th>Sec</th>
                      <th>Billable</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in callsModal.records" :key="c.inboundCallId || (c.callDt + c.inboundPhoneNumber)">
                      <td class="nowrap">{{ c.callDt || "—" }}</td>
                      <td>{{ c.inboundPhoneNumber || "—" }}</td>
                      <td>
                        <div>{{ c.targetName || "—" }}</div>
                        <small class="text-muted">{{ c.targetNumber || "" }}</small>
                        <span v-if="c.isPortalDuplicate" class="dup-pill">Dup</span>
                      </td>
                      <td>{{ c.portalDisposition || "—" }}</td>
                      <td>{{ c.connectedCallLengthInSeconds ?? "—" }}</td>
                      <td>
                        <span :class="c.isBillable ? 'bill-yes' : 'bill-no'">
                          {{ c.isBillable ? "Yes" : "No" }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!callsModal.records.length">
                      <td colspan="6" class="text-center text-muted py-3">No calls in this range</td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="callsModal.truncated" class="text-muted mt-2 mb-0" style="font-size: 11px">
                  Showing first 500 calls.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="tab !== 'hours' && tab !== 'history' && teamSummary.length" class="team-summary mt-4">
          <h4 class="section-title">Team summary</h4>
          <table class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th>Team</th>
                <th>Agents</th>
                <th>Transfers</th>
                <th>Billables</th>
                <th>Hours</th>
                <th>BPH</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in teamSummary" :key="t.teamName">
                <td>{{ t.teamName }}</td>
                <td>{{ t.agents }}</td>
                <td>{{ t.transfers }}</td>
                <td>{{ t.billables }}</td>
                <td>{{ fmtNum(t.hours) }}</td>
                <td :class="bphClass(t.bph)">{{ fmtBph(t.bph) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="tab === 'hours'" class="table-responsive mt-3">
          <div class="hours-actions">
            <button type="button" class="btn btn-primary" :disabled="savingHours" @click="saveHours">
              {{ savingHours ? "Saving…" : "Save hours & attendance" }}
            </button>
            <span class="hours-legend">
              <span class="missing-swatch" /> Missing hours (no dialed hours and not an absence)
            </span>
          </div>
          <table class="table table-bordered align-middle hours-att-table">
            <thead class="table-light">
              <tr>
                <th>Agent</th>
                <th v-for="d in hourDates" :key="d">{{ d }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredAgentsForHours" :key="'h-' + row.avatarId">
                <td>
                  <div class="agent-cell">
                    <strong>{{ row.fullName || row.username || row.avatarId }}</strong>
                    <small>{{ row.avatarId }}</small>
                  </div>
                </td>
                <td
                  v-for="d in hourDates"
                  :key="row.avatarId + d"
                  class="ha-cell"
                  :class="{ 'hours-missing-cell': isHoursMissing(row.avatarId, d) }"
                >
                  <div
                    class="ha-stack"
                    :class="attendanceTone(attendanceGrid[row.avatarId]?.[d])"
                  >
                    <label class="ha-field">
                      <span class="ha-label">Hours</span>
                      <input
                        type="number"
                        min="0"
                        step="0.25"
                        class="ha-input"
                        :value="hoursGrid[row.avatarId]?.[d] ?? ''"
                        @input="onHoursInput(row.avatarId, d, $event.target.value)"
                        placeholder="0"
                      />
                    </label>
                    <label class="ha-field">
                      <span class="ha-label">Status</span>
                      <select
                        class="ha-select"
                        :value="attendanceGrid[row.avatarId]?.[d] || ''"
                        @change="onAttendanceChange(row.avatarId, d, $event.target.value)"
                      >
                        <option value="">Not set</option>
                        <option v-for="s in attendanceStatuses" :key="s" :value="s">
                          {{ attendanceLabel(s) }}
                        </option>
                      </select>
                    </label>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
import UserMenu from "@/components/UserMenu.vue";
import AutoRefresh from "@/components/AutoRefresh.vue";

function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function enumerateDates(from, to) {
  const out = [];
  const cur = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, "0");
    const day = String(cur.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${day}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default {
  components: { UserMenu, AutoRefresh },
  data() {
    return {
      userName: localStorage.getItem("username") || "User",
      roles: [],
      tab: "daily",
      dateFrom: daysAgoIso(6),
      dateTo: todayIso(),
      teamId: "",
      agentFilter: "",
      teams: [],
      loading: false,
      liveRefreshing: false,
      refreshRate: 300000,
      lastLoadedAt: null,
      lastCgReportsPullAt: null,
      savingHours: false,
      savingInf: false,
      savingAdj: false,
      errorMessage: "",
      okMessage: "",
      perf: null,
      bonusData: null,
      hoursGrid: {},
      attendanceGrid: {},
      dirtyHours: {},
      hourAgents: [],
      attendanceStatuses: [
        "present",
        "partial",
        "authorized_absence",
        "unauthorized_absence",
        "ncns",
        "suspension",
      ],
      infForm: {
        avatarId: "",
        workDate: todayIso(),
        severity: "minor",
        infractionType: "other",
        notes: "",
      },
      adjForm: {
        avatarId: "",
        periodFrom: daysAgoIso(6),
        periodTo: todayIso(),
        amount: 0,
        reason: "",
      },
      callsModal: null,
      showUnmatchedPanel: false,
      assignAgentModal: null,
      assignAgentChoice: "",
      assignAgentFilter: "",
      assignAgentSubmitting: false,
      historyRecords: [],
      historyCategories: [],
      historyCategory: "",
      historySearch: "",
    };
  },
  computed: {
    periods() {
      return this.perf?.periods || [];
    },
    teamSummary() {
      return this.perf?.teamSummary || [];
    },
    unmatchedList() {
      return Array.isArray(this.perf?.unmatched) ? this.perf.unmatched : [];
    },
    filteredAssignAgents() {
      const q = String(this.assignAgentFilter || "").trim().toLowerCase();
      const rows = this.hourAgents || [];
      if (!q) return rows;
      return rows.filter(
        (a) =>
          String(a.fullName || "").toLowerCase().includes(q) ||
          String(a.username || "").toLowerCase().includes(q) ||
          String(a.avatarId || "").toLowerCase().includes(q) ||
          String(a.teamName || "").toLowerCase().includes(q)
      );
    },
    dispositionTotalsMap() {
      const fromApi = this.perf?.dispositionTotals || {};
      if (fromApi && Object.keys(fromApi).length) return fromApi;
      // Fallback: aggregate from agent/period payloads if totals key missing
      const acc = {};
      for (const row of this.perf?.agents || []) {
        for (const [code, n] of Object.entries(row.dispositions || {})) {
          acc[code] = (acc[code] || 0) + Number(n || 0);
        }
        for (const cell of Object.values(row.periods || {})) {
          for (const [code, n] of Object.entries(cell.dispositions || {})) {
            acc[code] = (acc[code] || 0) + Number(n || 0);
          }
        }
      }
      return acc;
    },
    dispositionColumns() {
      const totals = this.dispositionTotalsMap;
      return Object.keys(totals).sort((a, b) => {
        const diff = Number(totals[b] || 0) - Number(totals[a] || 0);
        if (diff !== 0) return diff;
        return String(a).localeCompare(String(b));
      });
    },
    filteredAgents() {
      const q = (this.agentFilter || "").trim().toLowerCase();
      const rows = this.perf?.agents || [];
      if (!q) return rows;
      return rows.filter(
        (r) =>
          String(r.fullName || "").toLowerCase().includes(q) ||
          String(r.username || "").toLowerCase().includes(q) ||
          String(r.avatarId || "").toLowerCase().includes(q)
      );
    },
    hourDates() {
      return enumerateDates(this.dateFrom, this.dateTo);
    },
    filteredAgentsForHours() {
      const q = (this.agentFilter || "").trim().toLowerCase();
      let rows = this.hourAgents;
      if (this.teamId) {
        rows = rows.filter((r) => String(r.teamId || "") === String(this.teamId));
      }
      if (!q) return rows;
      return rows.filter(
        (r) =>
          String(r.fullName || "").toLowerCase().includes(q) ||
          String(r.username || "").toLowerCase().includes(q) ||
          String(r.avatarId || "").toLowerCase().includes(q)
      );
    },
    filteredBonusAgents() {
      const q = (this.agentFilter || "").trim().toLowerCase();
      const rows = this.bonusData?.agents || [];
      if (!q) return rows;
      return rows.filter(
        (r) =>
          String(r.fullName || "").toLowerCase().includes(q) ||
          String(r.username || "").toLowerCase().includes(q) ||
          String(r.avatarId || "").toLowerCase().includes(q)
      );
    },
    bonusInfractions() {
      return this.bonusData?.infractions || [];
    },
    bonusAdjustments() {
      return this.bonusData?.adjustments || [];
    },
    lastLoadedLabel() {
      return this.formatNyTime(this.lastLoadedAt);
    },
    lastCgReportsLabel() {
      return this.formatNyTime(this.lastCgReportsPullAt);
    },
  },
  async mounted() {
    const token = localStorage.getItem("token");
    if (!token) {
      this.$router.push("/login");
      return;
    }
    await this.check_role();
    if (!this.roles.includes("admin") && !this.roles.includes("manager")) {
      this.$router.push("/calls");
      return;
    }
    document.title = "Agent Performance";
    this._onDispEsc = (e) => {
      if (e.key !== "Escape") return;
      if (this.assignAgentModal) this.closeAssignAgentModal();
      else if (this.callsModal) this.closeCallsDrill();
    };
    window.addEventListener("keydown", this._onDispEsc);
    await this.loadTeams();
    await this.loadPerformance();
  },
  beforeUnmount() {
    if (this._onDispEsc) window.removeEventListener("keydown", this._onDispEsc);
  },
  methods: {
    authHeaders() {
      return { Authorization: `Bearer ${localStorage.getItem("token")}` };
    },
    formatNyTime(value) {
      if (!value) return "";
      try {
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleString("en-US", {
          timeZone: "America/New_York",
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch (e) {
        return "";
      }
    },
    markLoaded(extra = {}) {
      this.lastLoadedAt = Date.now();
      if (extra.lastCgReportsPullAt) {
        this.lastCgReportsPullAt = extra.lastCgReportsPullAt;
      }
    },
    async onLiveRefresh() {
      if (this.loading || this.liveRefreshing) return;
      if (this.assignAgentModal || this.callsModal) return;
      if (this.tab === "hours" && Object.keys(this.dirtyHours || {}).length) return;
      this.liveRefreshing = true;
      try {
        if (this.tab === "hours") await this.loadHoursGrid({ quiet: true });
        else if (this.tab === "bonuses") await this.loadBonuses({ quiet: true });
        else if (this.tab === "history") await this.loadHistory({ quiet: true });
        else await this.loadPerformance({ quiet: true });
      } finally {
        this.liveRefreshing = false;
      }
    },
    historyCategoryLabel(id) {
      const hit = (this.historyCategories || []).find((c) => c.id === id);
      if (hit) return hit.label;
      const map = {
        hours: "Hours & Attendance",
        ownership: "Assign agent",
        target: "Target / Duplicate",
        infractions: "Infractions",
        adjustments: "Bonus adjustments",
        teams: "Teams / agent team",
        sync: "Agent sync",
      };
      return map[id] || id || "other";
    },
    async loadHistory(opts = {}) {
      const quiet = Boolean(opts.quiet);
      if (!quiet) {
        this.loading = true;
        this.errorMessage = "";
      }
      try {
        const params = new URLSearchParams({
          date_from: this.dateFrom,
          date_to: this.dateTo,
          limit: "300",
        });
        if (this.historyCategory) params.set("category", this.historyCategory);
        if (this.historySearch) params.set("search", this.historySearch.trim().toLowerCase());
        const res = await fetch(`/api/agent_performance/history?${params.toString()}`, {
          headers: this.authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!quiet) {
            this.errorMessage = data.error || "Failed to load history";
            this.historyRecords = [];
          }
          return;
        }
        this.historyRecords = data.records || [];
        if (Array.isArray(data.categories) && data.categories.length) {
          this.historyCategories = data.categories;
        }
        this.markLoaded();
      } finally {
        if (!quiet) this.loading = false;
      }
    },
    async check_role() {
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: this.authHeaders(),
      });
      const data = await res.json();
      this.roles = data.roles || [];
    },
    cell(row, key) {
      return (row.periods && row.periods[key]) || { hours: 0, billables: 0, bph: null };
    },
    dispositionTotal(code) {
      return Number((this.dispositionTotalsMap || {})[code] || 0);
    },
    dispositionEntries(map) {
      return Object.keys(map || {})
        .map((code) => ({ code, count: Number(map[code] || 0) }))
        .filter((x) => x.count > 0)
        .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
    },
    agentDispositionMap(row) {
      if (row?.dispositions && Object.keys(row.dispositions).length) {
        const out = {};
        for (const [code, n] of Object.entries(row.dispositions)) {
          const v = Number(n || 0);
          if (v > 0) out[code] = v;
        }
        if (Object.keys(out).length) return out;
      }
      const acc = {};
      for (const p of this.periods) {
        const d = this.cell(row, p.key).dispositions || {};
        for (const [code, n] of Object.entries(d)) {
          const v = Number(n || 0);
          if (v > 0) acc[code] = (acc[code] || 0) + v;
        }
      }
      return acc;
    },
    cellDrillable(row, periodKey) {
      const c = this.cell(row, periodKey) || {};
      if (Number(c.billables || 0) > 0) return true;
      if (Number(c.transfers || 0) > 0) return true;
      const disp = c.dispositions || {};
      return Object.keys(disp).some((code) => Number(disp[code] || 0) > 0);
    },
    periodDateRange(period) {
      if (!period?.key) {
        return { from: this.dateFrom, to: this.dateTo };
      }
      const from = String(period.key).slice(0, 10);
      if (this.tab === "weekly") {
        const d = new Date(`${from}T00:00:00`);
        d.setDate(d.getDate() + 6);
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return { from, to: `${d.getFullYear()}-${m}-${day}` };
      }
      return { from, to: from };
    },
    drillDispItems(row, period) {
      if (period?.key) {
        return this.dispositionEntries(this.cell(row, period.key).dispositions || {});
      }
      return this.dispositionEntries(this.agentDispositionMap(row));
    },
    async openCallsDrill(row, opts = {}) {
      const period = opts.period || null;
      const range = this.periodDateRange(period);
      const billableOnly = Boolean(opts.billableOnly);
      this.callsModal = {
        agentName: row.fullName || row.username || row.avatarId,
        avatarId: row.avatarId,
        dateFrom: range.from,
        dateTo: range.to,
        periodLabel: period?.label || `${range.from} → ${range.to}`,
        billableOnly,
        dispItems: this.drillDispItems(row, period),
        loading: true,
        error: "",
        records: [],
        total: 0,
        billables: 0,
        truncated: false,
      };
      await this.loadCallsDrill();
    },
    closeCallsDrill() {
      this.callsModal = null;
    },
    async setCallsBillableOnly(billableOnly) {
      if (!this.callsModal || this.callsModal.billableOnly === billableOnly) return;
      this.callsModal.billableOnly = billableOnly;
      await this.loadCallsDrill();
    },
    async loadCallsDrill() {
      if (!this.callsModal?.avatarId) return;
      this.callsModal.loading = true;
      this.callsModal.error = "";
      try {
        const res = await fetch("/api/agent_performance/calls", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            avatarId: this.callsModal.avatarId,
            reportStart: this.callsModal.dateFrom,
            reportEnd: this.callsModal.dateTo,
            billableOnly: this.callsModal.billableOnly,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.callsModal.error = data.error || "Failed to load calls";
          this.callsModal.records = [];
          this.callsModal.total = 0;
          this.callsModal.billables = 0;
          return;
        }
        this.callsModal.records = data.records || [];
        this.callsModal.total = Number(data.total || 0);
        this.callsModal.billables = Number(data.billables || 0);
        this.callsModal.truncated = Boolean(data.truncated);
      } catch (e) {
        this.callsModal.error = "Failed to load calls";
      } finally {
        if (this.callsModal) this.callsModal.loading = false;
      }
    },
    openInRingbaCalls() {
      if (!this.callsModal) return;
      const q = new URLSearchParams({
        date_from: this.callsModal.dateFrom,
        date_to: this.callsModal.dateTo,
        avatar_id: this.callsModal.avatarId || "",
      });
      const route = this.$router.resolve({ path: "/calls", query: Object.fromEntries(q) });
      window.open(route.href, "_blank");
    },
    agentOptionLabel(a) {
      const name = String(a?.fullName || "").trim();
      const user = String(a?.username || "").trim();
      const id = String(a?.avatarId || "").trim();
      const team = String(a?.teamName || "").trim();
      const parts = [name || user || id];
      if (user && name) parts.push(user);
      if (id) parts.push(id);
      if (team) parts.push(team);
      return parts.filter(Boolean).join(" · ");
    },
    async openAssignAgentModal(row) {
      this.assignAgentModal = row || null;
      this.assignAgentChoice = "";
      this.assignAgentFilter = "";
      this.showUnmatchedPanel = true;
      await this.ensureAgentsLoaded();
    },
    closeAssignAgentModal() {
      this.assignAgentModal = null;
      this.assignAgentChoice = "";
      this.assignAgentFilter = "";
      this.assignAgentSubmitting = false;
    },
    async submitAssignAgent() {
      if (!this.assignAgentModal?.inboundCallId || !this.assignAgentChoice || this.assignAgentSubmitting) {
        return;
      }
      this.assignAgentSubmitting = true;
      this.errorMessage = "";
      try {
        const selected = (this.hourAgents || []).find(
          (a) => a.avatarId === this.assignAgentChoice
        );
        const res = await fetch("/api/calllogs/agent_override", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({
            inboundCallId: this.assignAgentModal.inboundCallId,
            avatarId: this.assignAgentChoice,
            username: selected?.username || undefined,
            fullName: selected?.fullName || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.errorMessage = data.error || "Failed to assign agent";
          return;
        }
        this.okMessage = `Assigned agent ${this.assignAgentChoice} to call ${this.assignAgentModal.inboundCallId}`;
        this.closeAssignAgentModal();
        await this.loadPerformance();
        if (!this.unmatchedList.length) this.showUnmatchedPanel = false;
      } finally {
        this.assignAgentSubmitting = false;
      }
    },
    fmtNum(v) {
      const n = Number(v || 0);
      return Number.isInteger(n) ? String(n) : n.toFixed(2);
    },
    fmtBph(v) {
      if (v == null || v === "") return "N/A";
      return Number(v).toFixed(3);
    },
    bphClass(v) {
      if (v == null) return "bph-na";
      if (v >= 0.4) return "bph-good";
      if (v >= 0.36) return "bph-mid";
      return "bph-low";
    },
    switchTab(tab) {
      this.tab = tab;
      if (tab === "hours") this.loadHoursGrid();
      else if (tab === "bonuses") this.loadBonuses();
      else if (tab === "history") this.loadHistory();
      else this.loadPerformance();
    },
    fmtMoney(v) {
      const n = Number(v || 0);
      return n.toFixed(2);
    },
    agentName(avatarId) {
      const a = (this.hourAgents || []).find((x) => x.avatarId === avatarId);
      if (a) return a.fullName || a.username || avatarId;
      const b = (this.bonusData?.agents || []).find((x) => x.avatarId === avatarId);
      return b?.fullName || b?.username || avatarId;
    },
    bonusPrimaryWeek(row) {
      const weeks = row?.weeks || [];
      return weeks[0] || null;
    },
    bonusEligibleSum(row) {
      return (row?.weeks || []).reduce((s, w) => s + Number(w.eligibleDailySum || 0), 0);
    },
    bonusAdjSum(row) {
      return (row?.weeks || []).reduce((s, w) => s + Number(w.adjustmentsSum || 0), 0);
    },
    bonusReasons(row) {
      const out = [];
      for (const w of row?.weeks || []) {
        out.push(...(w.weekForfeitReasons || []));
      }
      for (const d of Object.values(row?.days || {})) {
        if (!d?.dayForfeited) continue;
        out.push(
          ...(d.dayForfeitReasons || []).map((r) => `${d.date}: ${r}`)
        );
      }
      return [...new Set(out)].slice(0, 8);
    },
    async ensureAgentsLoaded() {
      if (this.hourAgents.length) return;
      const res = await fetch("/api/agents", { headers: this.authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) this.hourAgents = data.records || [];
    },
    async loadBonuses(opts = {}) {
      const quiet = Boolean(opts.quiet);
      if (!quiet) {
        this.loading = true;
        this.errorMessage = "";
        this.okMessage = "";
      }
      try {
        await this.ensureAgentsLoaded();
        const body = {
          reportStart: this.dateFrom,
          reportEnd: this.dateTo,
        };
        if (this.teamId) body.teamId = Number(this.teamId);
        const res = await fetch("/api/agent_bonuses", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!quiet) {
            this.errorMessage = data.error || "Failed to load bonuses";
            this.bonusData = null;
          }
          return;
        }
        this.bonusData = data;
        this.infForm.workDate = this.dateTo;
        this.adjForm.periodFrom = this.dateFrom;
        this.adjForm.periodTo = this.dateTo;
        this.markLoaded();
      } finally {
        if (!quiet) this.loading = false;
      }
    },
    async saveInfraction() {
      if (!this.infForm.avatarId || !this.infForm.workDate) {
        this.errorMessage = "Agent and date are required";
        return;
      }
      this.savingInf = true;
      this.errorMessage = "";
      try {
        const res = await fetch("/api/agent_infractions", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(this.infForm),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.errorMessage = data.error || "Failed to add infraction";
          return;
        }
        this.okMessage = "Infraction saved";
        this.infForm.notes = "";
        await this.loadBonuses();
      } finally {
        this.savingInf = false;
      }
    },
    async deleteInfraction(id) {
      if (!window.confirm("Delete this infraction?")) return;
      const res = await fetch(`/api/agent_infractions/${id}`, {
        method: "DELETE",
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.errorMessage = "Failed to delete infraction";
        return;
      }
      this.okMessage = "Infraction deleted";
      await this.loadBonuses();
    },
    async saveAdjustment() {
      if (!this.adjForm.avatarId || !this.adjForm.periodFrom) {
        this.errorMessage = "Agent and period are required";
        return;
      }
      this.savingAdj = true;
      this.errorMessage = "";
      try {
        const res = await fetch("/api/agent_bonus_adjustments", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(this.adjForm),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.errorMessage = data.error || "Failed to add adjustment";
          return;
        }
        this.okMessage = "Adjustment saved";
        this.adjForm.amount = 0;
        this.adjForm.reason = "";
        await this.loadBonuses();
      } finally {
        this.savingAdj = false;
      }
    },
    async deleteAdjustment(id) {
      if (!window.confirm("Delete this adjustment?")) return;
      const res = await fetch(`/api/agent_bonus_adjustments/${id}`, {
        method: "DELETE",
        headers: this.authHeaders(),
      });
      if (!res.ok) {
        this.errorMessage = "Failed to delete adjustment";
        return;
      }
      this.okMessage = "Adjustment deleted";
      await this.loadBonuses();
    },
    async loadTeams() {
      const res = await fetch("/api/teams", { headers: this.authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      this.teams = data.records || [];
    },
    async loadPerformance(opts = {}) {
      const quiet = Boolean(opts.quiet);
      if (this.tab === "hours") {
        await this.loadHoursGrid(opts);
        return;
      }
      if (this.tab === "bonuses") {
        await this.loadBonuses(opts);
        return;
      }
      if (this.tab === "history") {
        await this.loadHistory(opts);
        return;
      }
      if (!quiet) {
        this.loading = true;
        this.errorMessage = "";
        this.okMessage = "";
      }
      try {
        const body = {
          reportStart: this.dateFrom,
          reportEnd: this.dateTo,
          mode: this.tab === "weekly" ? "weekly" : "daily",
        };
        if (this.teamId) body.teamId = Number(this.teamId);
        const res = await fetch("/api/agent_performance", {
          method: "POST",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (!quiet) {
            this.errorMessage = data.error || "Failed to load performance";
            this.perf = null;
          }
          return;
        }
        this.perf = data;
        this.markLoaded({ lastCgReportsPullAt: data.lastCgReportsPullAt || null });
      } finally {
        if (!quiet) this.loading = false;
      }
    },
    attendanceLabel(s) {
      const map = {
        present: "Present",
        partial: "Partial",
        authorized_absence: "Auth. absence",
        unauthorized_absence: "Unauth. absence",
        ncns: "NCNS",
        suspension: "Suspension",
      };
      return map[s] || s;
    },
    attendanceTone(status) {
      const s = String(status || "").toLowerCase();
      if (!s) return "tone-empty";
      if (s === "present") return "tone-present";
      if (s === "partial") return "tone-partial";
      if (s === "authorized_absence") return "tone-auth";
      if (s === "unauthorized_absence" || s === "ncns" || s === "suspension") {
        return "tone-bad";
      }
      return "tone-empty";
    },
    excusedAttendance(status) {
      return ["authorized_absence", "unauthorized_absence", "ncns", "suspension"].includes(
        String(status || "").toLowerCase()
      );
    },
    isHoursMissing(avatarId, date) {
      const hrs = this.hoursGrid[avatarId]?.[date];
      const status = this.attendanceGrid[avatarId]?.[date];
      if (this.excusedAttendance(status)) return false;
      const n = hrs === null || hrs === undefined || hrs === "" ? 0 : Number(hrs);
      return !(n > 0);
    },
    onHoursInput(avatarId, date, raw) {
      if (!this.hoursGrid[avatarId]) this.hoursGrid[avatarId] = {};
      if (raw === "" || raw === null) {
        this.hoursGrid[avatarId][date] = null;
      } else {
        this.hoursGrid[avatarId][date] = Number(raw);
      }
      const key = `${avatarId}|${date}`;
      this.dirtyHours[key] = { ...(this.dirtyHours[key] || {}), hours: true };
    },
    onAttendanceChange(avatarId, date, value) {
      if (!this.attendanceGrid[avatarId]) this.attendanceGrid[avatarId] = {};
      this.attendanceGrid[avatarId][date] = value || "";
      const key = `${avatarId}|${date}`;
      this.dirtyHours[key] = { ...(this.dirtyHours[key] || {}), attendance: true };
    },
    async loadHoursGrid(opts = {}) {
      const quiet = Boolean(opts.quiet);
      if (quiet && Object.keys(this.dirtyHours || {}).length) return;
      if (!quiet) {
        this.loading = true;
        this.errorMessage = "";
      }
      try {
        const [agentsRes, hoursRes] = await Promise.all([
          fetch("/api/agents", { headers: this.authHeaders() }),
          fetch(
            `/api/agent_hours?date_from=${encodeURIComponent(this.dateFrom)}&date_to=${encodeURIComponent(this.dateTo)}`,
            { headers: this.authHeaders() }
          ),
        ]);
        const agentsData = await agentsRes.json().catch(() => ({}));
        const hoursData = await hoursRes.json().catch(() => ({}));
        if (!agentsRes.ok) {
          if (!quiet) this.errorMessage = "Failed to load agents";
          return;
        }
        this.hourAgents = agentsData.records || [];
        if (Array.isArray(hoursData.attendanceStatuses) && hoursData.attendanceStatuses.length) {
          this.attendanceStatuses = hoursData.attendanceStatuses;
        }
        const grid = {};
        const att = {};
        for (const a of this.hourAgents) {
          grid[a.avatarId] = {};
          att[a.avatarId] = {};
          for (const d of this.hourDates) {
            grid[a.avatarId][d] = null;
            att[a.avatarId][d] = "";
          }
        }
        for (const h of hoursData.records || []) {
          if (!grid[h.avatarId]) {
            grid[h.avatarId] = {};
            att[h.avatarId] = {};
          }
          grid[h.avatarId][h.workDate] = Number(h.hours);
          att[h.avatarId][h.workDate] = h.attendanceStatus || "";
        }
        this.hoursGrid = grid;
        this.attendanceGrid = att;
        this.dirtyHours = {};
        this.markLoaded();
      } finally {
        if (!quiet) this.loading = false;
      }
    },
    async saveHours() {
      this.savingHours = true;
      this.errorMessage = "";
      this.okMessage = "";
      try {
        const entries = [];
        for (const a of this.filteredAgentsForHours) {
          for (const d of this.hourDates) {
            const key = `${a.avatarId}|${d}`;
            const dirty = this.dirtyHours[key];
            if (!dirty) continue;
            const entry = { avatarId: a.avatarId, workDate: d };
            if (dirty.hours) {
              const v = this.hoursGrid[a.avatarId]?.[d];
              if (v === null || v === undefined || v === "") {
                // hours cleared — skip hours key; attendance-only still ok
              } else {
                entry.hours = Number(v);
              }
            }
            if (dirty.attendance) {
              entry.attendanceStatus = this.attendanceGrid[a.avatarId]?.[d] || null;
            }
            if (!("hours" in entry) && !("attendanceStatus" in entry)) continue;
            entries.push(entry);
          }
        }
        if (!entries.length) {
          this.errorMessage = "Nothing to save — change hours or attendance first";
          return;
        }
        const res = await fetch("/api/agent_hours", {
          method: "PUT",
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ entries }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.errorMessage = data.error || "Failed to save hours & attendance";
          return;
        }
        this.okMessage = `Saved ${data.saved} entries`;
        this.dirtyHours = {};
        await this.loadHoursGrid();
      } finally {
        this.savingHours = false;
      }
    },
  },
};
</script>

<style scoped>
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
  position: relative;
}

.pw-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.pw-loading-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.pw-spinner {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  border-top-color: #4e54c8;
  animation: pw-spin 0.8s linear infinite;
}

.pw-loading-text {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

@keyframes pw-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.tab-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 600;
  color: #334155;
}

.tab-btn.active {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: #fff;
  border-color: transparent;
}

.toolbar.panel {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: end;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
}

.toolbar label {
  display: grid;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.toolbar .form-control,
.toolbar .form-select {
  min-width: 150px;
  height: 38px;
}

.load-btn {
  height: 38px;
  min-width: 100px;
}

.meta-row {
  margin-top: 12px;
  font-size: 13px;
  color: #475569;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.unmatched-toggle {
  margin-left: 4px;
}
.unmatched-panel {
  margin-top: 10px;
  margin-bottom: 4px;
}
.unmatched-table {
  font-size: 12px;
}
.unmatched-table .mono-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  max-width: 180px;
  word-break: break-all;
}
.assign-meta {
  display: grid;
  gap: 4px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #475569;
}
.assign-field {
  display: grid;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.ap-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.agent-cell {
  display: grid;
  gap: 2px;
}

.agent-cell small {
  color: #64748b;
  font-size: 11px;
}

.period-head {
  background: #eef2ff !important;
}

.section-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 600;
  color: #334155;
}

.bph-good { color: #15803d; font-weight: 600; }
.bph-mid { color: #a16207; font-weight: 600; }
.bph-low { color: #b91c1c; font-weight: 600; }
.bph-na { color: #94a3b8; }

.hours-actions {
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}
.hours-legend {
  font-size: 12px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.missing-swatch {
  width: 12px;
  height: 12px;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 3px;
  display: inline-block;
}
.hours-att-table th {
  font-size: 12px;
  color: #475569;
}
.ha-cell {
  min-width: 132px;
  padding: 8px !important;
  vertical-align: top !important;
  background: #fff;
}
.ha-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  border-left-width: 3px;
  border-left-color: #cbd5e1;
}
.ha-stack.tone-present { border-left-color: #22c55e; background: #f0fdf4; }
.ha-stack.tone-partial { border-left-color: #eab308; background: #fefce8; }
.ha-stack.tone-auth { border-left-color: #3b82f6; background: #eff6ff; }
.ha-stack.tone-bad { border-left-color: #ef4444; background: #fef2f2; }
.ha-stack.tone-empty { border-left-color: #94a3b8; }

.ha-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin: 0;
}
.ha-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #64748b;
}
.ha-input,
.ha-select {
  width: 100%;
  height: 30px;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  font-size: 12px;
  line-height: 1.2;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ha-input:focus,
.ha-select:focus {
  border-color: #4e54c8;
  box-shadow: 0 0 0 3px rgba(78, 84, 200, 0.15);
}
.ha-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M3 4.5L6 8l3-3.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 24px;
}
.hours-missing-cell {
  background: #fff5f5 !important;
}
.hours-missing-cell .ha-stack.tone-empty {
  border-color: #fecaca;
  border-left-color: #f87171;
  background: #fff1f2;
}
.missing-flag {
  color: #b91c1c;
  font-weight: 700;
  margin-left: 2px;
}

.disposition-totals {
  margin-top: 10px;
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.disposition-totals strong {
  font-size: 13px;
  color: #1e293b;
}
.disp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.disp-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  font-size: 12px;
}
.disp-code {
  color: #475569;
  font-weight: 600;
}
.disp-n {
  color: #1e293b;
  font-weight: 700;
  min-width: 1.2em;
  text-align: right;
}
.transfers-head {
  min-width: 72px;
  text-align: center;
}
.transfers-cell {
  text-align: center;
}
.transfers-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-weight: 700;
  font-size: 13px;
  padding: 2px 8px;
  border-radius: 6px;
  cursor: pointer;
  line-height: 1.2;
}
.transfers-btn:hover:not(:disabled) {
  background: #eff6ff;
  color: #1d4ed8;
  text-decoration: underline;
}
.transfers-btn:disabled,
.transfers-btn-muted {
  color: #0f172a;
  font-weight: 400;
  cursor: default;
  text-decoration: none;
}
.ap-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.ap-modal {
  width: min(420px, 100%);
  max-height: min(80vh, 560px);
  overflow: auto;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
}
.ap-modal-wide {
  width: min(820px, 100%);
  max-height: min(85vh, 720px);
}
.calls-drill-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 12px 0 10px;
}
.calls-drill-filters {
  display: flex;
  gap: 6px;
}
.calls-drill-table-wrap {
  max-height: 48vh;
  overflow: auto;
}
.calls-drill-table {
  font-size: 12px;
}
.calls-drill-table .nowrap {
  white-space: nowrap;
}
.dup-pill {
  display: inline-block;
  margin-left: 4px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 10px;
  font-weight: 600;
}
.bill-yes {
  color: #15803d;
  font-weight: 700;
}
.bill-no {
  color: #94a3b8;
}
.ap-modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e2e8f0;
}
.ap-modal-head h3 {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}
.ap-modal-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
}
.ap-modal-close {
  border: none;
  background: #f1f5f9;
  color: #475569;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
.ap-modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}
.ap-modal-body {
  padding: 14px 16px 16px;
}
.ap-modal-chips {
  margin-bottom: 4px;
}
.ap-modal-days {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.ap-modal-day {
  padding: 10px 10px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
}
.ap-modal-day-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}
.ap-modal-day-n {
  color: #64748b;
  font-weight: 700;
}

.bonus-rules {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #475569;
}
.bonus-rules strong { color: #1e293b; }
.bonus-reasons {
  max-width: 280px;
  white-space: normal !important;
}
.reason-chip {
  display: inline-block;
  margin: 1px 3px 1px 0;
  padding: 2px 6px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #334155;
  font-size: 10px;
}
.bonus-manage { margin-top: 8px; }
.bonus-form {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.sev-minor {
  color: #a16207;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
}
.sev-grave {
  color: #b91c1c;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
}

.ap-table {
  font-size: 12px;
}

.ap-table th,
.ap-table td {
  white-space: nowrap;
  vertical-align: middle !important;
}

.toolbar-live {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}
.ap-updated-dim {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
  white-space: normal;
  line-height: 1.35;
  max-width: 420px;
}
.history-table {
  font-size: 12px;
}
.history-table .nowrap {
  white-space: nowrap;
}
.history-cat {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
}
.history-action {
  white-space: normal !important;
  word-break: break-word;
  max-width: 640px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #334155;
}
</style>
